import { BeanzConnectError } from "../errors";
import type { SkuRegionPrice } from "../schemas/enums/crossBorder.enum";
import { TermValue, WebStatus } from "../services";
import { ProductConnection } from "./connection";
import { Locale } from "./localization";
import type { IntensityPosition, IntensityZone } from "./intensity";
import { PimProductsCategories } from "./translation";

export interface Product {
  type: "product";
  id: string;
  localId?: string;
  serviceId?: string;
  title?: string;
  description?: string;
  process?: string;
  publishedAt?: string;
  isDecaf?: boolean;
  roastLevel?: string;
  // Beanz intensity score (roast colour). Product-level: both bag-size
  // variants of a coffee share one score. `intensityZone` is always derived
  // from `intensityPosition` via `zoneFromPosition`, never entered directly.
  intensityZone?: IntensityZone;
  intensityPosition?: IntensityPosition;
  // The roaster's answer to "most similar to": the name of the existing coffee
  // whose intensity they matched, sent as `Closest_Match_Coffee__c`. The scale
  // position is derived from that pick, so it is the only way to score a
  // coffee. Round-tripped from Salesforce so a resubmission keeps it.
  closestMatchCoffee?: string;
  brewMethod?: string[];
  brewStyle?: string;
  certifications?: string[];
  mainFlavorCategory?: string;
  secondaryFlavorCategory?: string;
  region?: string;
  country?: string;
  elevationStart?: number;
  elevationEnd?: number;
  varietal?: string;
  grind?: string[];
  qty?: number;
  tastingNotes?: string[];
  badges?: string[];
  character?: string;
  variants?: Variant[];
  images?: ProductImage[];
  comments?: string;
  status?: string;
  roasterNotes?: string;
  productType?: string;
  localizedFields?: LocalizedFields;
  ship_to?: string | null;
  // Salesforce `Item_numbers__c` — a ","/";"-delimited list of item numbers
  // (PIM SKUs), so the plural name reflects it can carry multiple values.
  item_numbers?: string | null;
  // Cross-border per-region prices, round-tripped from the product target
  // metadata. Absent for non-cross-border products.
  skuPrices?: SkuRegionPrice[];
}

export interface ProductReference {
  id: string;
  title: string;
  status: string;
  variantIds?: string[];
}

export type LocalizedFieldKey = "description" | "roasterNotes";

export type LocalizedFields = {
  [key in Locale]?: {
    [key in LocalizedFieldKey]: string | undefined;
  };
};

export interface SelectOption {
  label: string;
  value: string;
  isDisabled?: boolean;
}

export type TranslationCategory =
  | "BEAN-COFFEE-STYLE"
  | "BEAN-TYPE"
  | "BREW-METHOD"
  | "CERTIFICATION"
  | "COUNTRIES"
  | "DISCOVERY-FLAVOUR-CATEGORY"
  | "FLAVOUR-CATEGORY"
  | "GRIND-TYPE"
  | "PROCESS"
  | "REGION"
  | "ROAST-LEVEL"
  | "SECONDARY-FLAVOUR-CATEGORY"
  | "TASTING-NOTES"
  | "TERM"
  | "VARIETAL";

export type LocalizedProductConfigKeys =
  | "availability"
  | "bagSize"
  | "brewMethod"
  | "brewStyle"
  | "country"
  | "region"
  | "certifications"
  | "flavorCategories"
  | "secondaryFlavourCategories"
  | "grind"
  | "process"
  | "productType"
  | "roastLevel"
  | "tastingNotes"
  | "term"
  | "varietal";

export type CountriesOptionsByRegionKey = { [key: string]: SelectOption[] };
export type TranslationCategoriesMap = { [key in TranslationCategory]: LocalizedProductConfigKeys };
export type LocaleWithLocalisedProductConfig = { [key in Locale]?: LocalisedProductConfig };
export type LocaleWithPimProductsCategories = { [key in Locale]?: PimProductsCategories };

export interface LocalisedProductConfig {
  flavorCategories: SelectOption[];
  brewMethod: SelectOption[];
  brewStyle: SelectOption[];
  regionAndCountry: CountriesOptionsByRegionKey;
  region: SelectOption[];
  certifications: SelectOption[];
  secondaryFlavourCategories: SelectOption[];
  grind: SelectOption[];
  process: SelectOption[];
  productType: SelectOption[];
  roastLevel: SelectOption[];
  tastingNotes: SelectOption[];
  varietal: SelectOption[];
  term: SelectOption[];
}

export type ProductRegion = "Africa" | "Asia" | "Central America" | "North America" | "South America";

export interface ProductConfig {
  availability: string[];
  bagSize: string[];
  brewMethod: string[];
  brewStyle: string[];
  regionAndCountry: { [key: string]: string[] };
  certifications: string[];
  flavorCategories: string[];
  secondaryFlavourCategories: string[];
  grind: string[];
  process: string[];
  productType: string[];
  roastLevel: string[];
  tastingNotes: string[];
  badges: string[];
  varietal: string[];
  character: string[];
}

export interface ProductImage {
  src: string;
  height?: number;
  width?: number;
}

// Per-ship-to-channel snapshot for a variant. The product fetch returns one
// entry per channel the SKU ships to; `channel` encodes the destination country
// (e.g. "BDE"/"BDEMP" → DE, "BNLMP" → NL — see COUNTRY_TO_SALES_CHANNEL). Carries
// that country's current price/status/inventory so the approval-request popovers
// can build per-country `shipToCountries` payloads. Absent for non-cross-border
// SKUs, where the variant's own single-valued fields are used instead.
// export interface ShipToInfo {
//   channel: string;
//   webStatus?: WebStatus;
//   price?: number;
//   wholesalePrice?: number;
//   inventoryQuantity?: number;
//   activeSubscriberCount?: number;
//   totalInventory?: number;
//   expectedOutOfStockDate?: string;
// }

export interface Variant {
  type: "variant";
  id: string;
  productId?: string;
  title?: string;
  price?: string | number;
  wholesalePrice?: string | number;
  startDate?: string;
  endDate?: string;
  inventoryQuantity?: number;
  webStatus?: WebStatus;
  bagSize?: string;
  term?: TermValue;
  maxBagsShippedPerWeek?: number;
  weeklyInventory?: number;
  currency?: string;
  webPrice?: number;
  grind?: string[];
  activeSubscriberCount?: number;
  totalInventory?: number;
  expectedOutOfStockDate?: string;
  // Cross-border per-region prices for this variant's bag size. Computed in the
  // pricing UI (home region entered, other ship-to regions derived) and written
  // into the product target metadata on import. Absent for non-cross-border.
  skuPrices?: SkuRegionPrice[];
  // Per-ship-to-country breakdown (one entry per channel). Present for
  // cross-border SKUs; drives the per-country current values in approval-request
  // popovers. See ShipToInfo.
  shipToInfo?: ShipToInfo[];
}
export interface ShipToInfo {
  channel: string;
  price?: string | number;
  wholesalePrice?: string | number;
  startDate?: string;
  endDate?: string;
  inventoryQuantity?: number;
  webStatus?: WebStatus;
  maxBagsShippedPerWeek?: number;
  weeklyInventory?: number;
  currency?: string;
  webPrice?: number;
  activeSubscriberCount?: number;
  totalInventory?: number;
  expectedOutOfStockDate?: string;
}
export interface MultiChannelVariant {
  type: "variant";
  id: string;
  productId?: string;
  title?: string;
  bagSize?: string;
  term?: TermValue;
  grind?: string[];
  shipToInfo: ShipToInfo[];
}
export interface MultiChannelProduct extends Omit<Product, "variants"> {
  variants?: MultiChannelVariant[];
}

export interface FormVariant extends Variant {
  bagSize: string;
  // Per-bag-size cross-border ship-to destinations, entered on the whole-bean
  // variant and inherited by other grinds of the same bag size (like price/term).
  // A ";"-separated country string (e.g. "NL;DE"). Absent for non-cross-border.
  // ship-to is bag-size based, not account/product level.
  ship_to?: string | null;
  localGrindOverride?: string[];
  // mapDetectedGrindTo?: string; // the detected grind will be mapped to this value
  mapDetectedBagSizeTo?: string; // the detected bag size will be mapped to this value
  isProductSelectedForImport?: boolean;
  hasDetectedBagSize?: boolean; // is the bag size detected from the title
  hasDetectedGrindType?: boolean; // is the grind type detected from the title
  hasValidDetectedBagSize?: boolean; // is the detected bag size valid based on the region config
  hasValidDetectedGrindType?: boolean; // is the detected grind type valid based on the region config
  isImportedRemote?: boolean; // has the variant been imported to remote (Salesforce)
  totalMaxShortTermInventory?: number;
  hasDetectedGrindOverride?: boolean;
  hasDetectedBagSizeOverride?: boolean;
  isImportedLocalVariant?: boolean;
  index?: number;
  status?: string;
  isBagSizeVariantImported?: boolean;
  serviceId?: string;
  isVariantHasSameBagSize?: boolean;
  detectedGrindInfo?: DetectedGrindInfo[];
  // Cross-border bruto-entry toggles: whether the price the roaster typed for
  // their home region already includes VAT and/or KS. Drive net-price recovery.
  vatIncluded?: boolean;
  ksIncluded?: boolean;
}

export interface DetectedGrindInfo {
  grind: string;
  mapDetectedGrindTo?: string;
  isValidGrind: boolean;
}

export interface FormProduct extends Omit<
  Product,
  "certifications" | "varietal" | "badges" | "intensityPosition" | "intensityZone" | "closestMatchCoffee"
> {
  // Unlike `Product`, which leaves an unscored coffee's intensity absent, the
  // form stores it explicitly as `null` / `""` (see `mapProductToFormDefaults`
  // and `normaliseIntensityFields`) so a cleared score survives JSON-persisted
  // drafts. The keys stay optional: a legacy draft never wrote them at all.
  intensityPosition?: IntensityPosition | null;
  intensityZone?: IntensityZone | "";
  closestMatchCoffee?: string | null;
  uuid?: string;
  variants: FormVariant[];
  localVariants?: FormVariant[];
  localTitle?: string;
  connections?: ProductConnection[];
  process?: string;
  roastLevel?: string;
  productType: string;
  brewMethod?: string[];
  certifications?: string[] | null;
  badges?: string[] | null;
  character?: string;
  varietal?: string;
  imageUploads?: UploadImage[] | null;
  isProductFormValid: boolean;
  hasDraft?: boolean;
  hasVariantsDraft?: boolean;
  /**
   * Whether `intensityPosition` came from PIM itself (a beans manager has
   * approved it), computed before any draft is merged onto this product.
   * The Intensity section locks on this rather than re-deriving it from the
   * (possibly draft-overlaid) `intensityPosition` value directly, since a
   * still-provisional draft position looks identical to an approved one
   * once merged. Undefined when the product never went through that merge
   * (e.g. the ShipStation flow has no drafts to merge).
   */
  hasApprovedIntensity?: boolean;
}

export type DraftFormProduct = FormProduct;

export interface DropZoneFile extends FileWithPath {
  webKitRelativePath?: string;
  preview: string;
}

interface DOMFile extends Blob {
  readonly lastModified: number;
  readonly name: string;
}
export interface FileWithPath extends DOMFile {
  readonly path?: string;
}
export interface ImageUploadBasic {
  type: "url" | "file";
  width?: number;
  height?: number;
}

export interface UrlUploadImage extends ImageUploadBasic {
  type: "url";
  url: string;
  thumbnailUrl?: string;
  file?: never;
}

export interface FileUploadImage extends ImageUploadBasic {
  type: "file";
  url?: never;
  thumbnailUrl?: never;
  file: DropZoneFile;
}

export class ProductError extends BeanzConnectError {
  type = "ProductError";
}

export type UploadImage = UrlUploadImage | FileUploadImage;
